import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { Pagination } from "antd";
import { Col, Input, Row } from "reactstrap";
import Loader from "../../../components/Loader/Loader";
import isEmpty from "../../../validation/is-empty";
import { notification } from "../../../utils/utility";
import Filter from "../../../components/Filter/Filter";
import FeatureRequestFilters from "./feature-request-filters";
import FeatureRequestCard from "./FeatureRequestCard";
import FeatureRequestDetailModal from "./FeatureRequestDetailModal";
import FeatureRequestConfirmDeleteModal from "./FeatureRequestConfirmDeleteModal";
import { toggleFilter } from "../../../actions/filterActions";
import {
  getFeatureRequests,
  markFeatureRequestDone,
  reopenFeatureRequest,
  deleteFeatureRequest,
} from "../../../actions/super-admin/feature-request-actions";
import {
  FEATURE_REQUEST_CATEGORIES,
  FEATURE_REQUEST_PRIORITIES,
} from "../../../constants/featureRequest";

const FILTER_LABELS = {
  search: "Search",
  category: "Category",
  priority: "Priority",
  paid_status: "Subscription",
  is_active: "Activity",
};

const FeatureRequestList = ({
  status,
  filterTitle,
  emptyTitle,
  emptyMessage,
  filter,
  toggleFilter,
  getFeatureRequests,
  markFeatureRequestDone,
  reopenFeatureRequest,
  deleteFeatureRequest,
}) => {
  const isLibrary = status === "done";
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });
  const [data, setData] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [sortOrder, setSortOrder] = useState("desc");
  const [markingDoneId, setMarkingDoneId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const stats = useMemo(() => {
    const highPriority = data.filter((item) =>
      ["P1", "P2", "P3"].includes(item.priority)
    ).length;

    return {
      total: pagination.total || 0,
      paid: data.filter((item) => item.is_paid).length,
      active: data.filter((item) => item.is_active).length,
      highPriority,
    };
  }, [data, pagination.total]);

  const activeFilterChips = useMemo(() => {
    const { searchFilters } = filter;
    return Object.entries(searchFilters || {}).map(([key, value]) => {
      let label = value;

      if (key === "category") {
        label =
          FEATURE_REQUEST_CATEGORIES.find((item) => item.value === value)
            ?.label || value;
      }

      if (key === "priority") {
        label =
          FEATURE_REQUEST_PRIORITIES.find((item) => item.value === value)
            ?.label || value;
      }

      if (key === "paid_status") {
        label = value === "paid" ? "Paid users" : "Unpaid users";
      }

      if (key === "is_active") {
        label = value === "yes" ? "Active users" : "Inactive users";
      }

      return {
        key,
        label: `${FILTER_LABELS[key] || key}: ${label}`,
      };
    });
  }, [filter]);

  const prepareDataParam = (params) => {
    const sortField = isLibrary
      ? "marked_done_at"
      : isEmpty(params.sortField)
      ? "created_at"
      : params.sortField;

    let order = sortOrder;
    if (!isEmpty(params.sortOrder)) {
      order = params.sortOrder === "ascend" ? "asc" : "desc";
    }

    return {
      pageSize: params.pagination.pageSize,
      pageNumber: params.pagination.current,
      sortField,
      sortOrder: order,
      filter: {
        status,
        ...params.filters,
      },
    };
  };

  const fetchTableData = (params = {}) => {
    setIsDataLoading(true);
    getFeatureRequests({
      data: prepareDataParam(params),
      onSuccess: function (response) {
        setData(response.entity || []);
        setPagination({
          ...params.pagination,
          total: response.totalCount || 0,
        });
        setIsDataLoading(false);
      },
      onError: function () {
        setData([]);
        setIsDataLoading(false);
      },
    });
  };

  const handleTableChange = (paginationConfig, tableFilters, sorter) => {
    const appliedFilters =
      tableFilters && Object.keys(tableFilters).length
        ? tableFilters
        : filter.searchFilters;

    fetchTableData({
      sortField: sorter?.field,
      sortOrder: sorter?.order,
      pagination: paginationConfig,
      filters: appliedFilters,
    });
  };

  const filterRecords = () => {
    toggleFilter(false);
    handleTableChange(
      {
        ...pagination,
        current: 1,
      },
      {
        ...filter.searchFilters,
      },
      {}
    );
  };

  const clearFilterHandler = () => {
    handleTableChange(
      {
        ...pagination,
        current: 1,
      },
      {},
      {}
    );
  };

  const onPageChange = (page, pageSize) => {
    handleTableChange(
      {
        ...pagination,
        current: page,
        pageSize,
      },
      filter.searchFilters,
      {
        field: isLibrary ? "marked_done_at" : "created_at",
        order: sortOrder === "asc" ? "ascend" : "descend",
      }
    );
  };

  const onSortChange = (event) => {
    const value = event.target.value;
    setSortOrder(value);
    fetchTableData({
      pagination,
      filters: filter.searchFilters,
      sortField: isLibrary ? "marked_done_at" : "created_at",
      sortOrder: value === "asc" ? "ascend" : "descend",
    });
  };

  const handleMarkDone = (record) => {
    if (!record?.id || markingDoneId) {
      return;
    }

    setMarkingDoneId(record.id);
    markFeatureRequestDone({
      data: { id: record.id },
      onSuccess: () => {
        notification.success("Idea marked as done and moved to the library");
        setSelectedRecord(null);
        setMarkingDoneId(null);
        fetchTableData({
          pagination,
          filters: filter.searchFilters,
        });
      },
      onError: () => {
        notification.error("Could not mark this idea as done. Please try again.");
        setMarkingDoneId(null);
      },
    });
  };

  const handleReopen = (record) => {
    if (!record?.id || reopeningId) {
      return;
    }

    setReopeningId(record.id);
    reopenFeatureRequest({
      data: { id: record.id },
      onSuccess: () => {
        notification.success("Idea moved back to the inbox");
        setSelectedRecord(null);
        setReopeningId(null);
        fetchTableData({
          pagination,
          filters: filter.searchFilters,
        });
      },
      onError: () => {
        notification.error("Could not move this idea back to the inbox.");
        setReopeningId(null);
      },
    });
  };

  const handleRequestDelete = (record) => {
    setSelectedRecord(null);
    setDeleteCandidate(record);
  };

  const handleCancelDelete = () => {
    if (!deletingId) {
      setDeleteCandidate(null);
    }
  };

  const handleConfirmDelete = () => {
    if (!deleteCandidate?.id || deletingId) {
      return;
    }

    setDeletingId(deleteCandidate.id);
    deleteFeatureRequest({
      data: { id: deleteCandidate.id },
      onSuccess: () => {
        notification.success("Idea deleted permanently");
        setDeleteCandidate(null);
        setDeletingId(null);
        setSelectedRecord(null);
        fetchTableData({
          pagination,
          filters: filter.searchFilters,
        });
      },
      onError: () => {
        notification.error("Could not delete this idea. Please try again.");
        setDeletingId(null);
      },
    });
  };

  useEffect(() => {
    setPagination({ current: 1, pageSize: 10 });
    setSortOrder("desc");
    fetchTableData({
      pagination: { current: 1, pageSize: 10 },
      filters: filter.searchFilters,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  return (
    <>
      <Loader
        isShowLoader={
          isDataLoading || !!markingDoneId || !!reopeningId || !!deletingId
        }
      />

      <Row className="app-user-list">
        <Col sm="12">
          <Filter
            name={filterTitle}
            onFilterRecords={filterRecords}
            onClearFilterHandler={clearFilterHandler}
          >
            <FeatureRequestFilters />
          </Filter>
        </Col>

        <Col sm="12">
          <div className="fr-stats">
            <div className="fr-stat-card fr-stat-card--accent">
              <div className="fr-stat-card__label">
                {isLibrary ? "Completed ideas" : "Open ideas"}
              </div>
              <div className="fr-stat-card__value">{stats.total}</div>
              <div className="fr-stat-card__hint">Matching current filters</div>
            </div>
            <div className="fr-stat-card">
              <div className="fr-stat-card__label">High priority (P1-P3)</div>
              <div className="fr-stat-card__value">{stats.highPriority}</div>
              <div className="fr-stat-card__hint">On this page</div>
            </div>
            <div className="fr-stat-card">
              <div className="fr-stat-card__label">Paid submitters</div>
              <div className="fr-stat-card__value">{stats.paid}</div>
              <div className="fr-stat-card__hint">On this page</div>
            </div>
            <div className="fr-stat-card">
              <div className="fr-stat-card__label">Active users</div>
              <div className="fr-stat-card__value">{stats.active}</div>
              <div className="fr-stat-card__hint">On this page</div>
            </div>
          </div>

          <div className="fr-toolbar">
            <div className="fr-toolbar__meta">
              {stats.total} request{stats.total === 1 ? "" : "s"} found
            </div>
            <div className="fr-toolbar__sort">
              <Input type="select" value={sortOrder} onChange={onSortChange}>
                <option value="desc">
                  {isLibrary ? "Recently completed" : "Newest first"}
                </option>
                <option value="asc">
                  {isLibrary ? "Oldest completed" : "Oldest first"}
                </option>
              </Input>
            </div>
          </div>

          {activeFilterChips.length > 0 && (
            <div className="fr-filter-chips">
              {activeFilterChips.map((chip) => (
                <span className="fr-chip" key={chip.key}>
                  {chip.label}
                </span>
              ))}
            </div>
          )}

          {data.length === 0 && !isDataLoading ? (
            <div className="fr-empty">
              <h4>{emptyTitle}</h4>
              <p>{emptyMessage}</p>
            </div>
          ) : (
            <div className="fr-list">
              {data.map((record) => (
                <FeatureRequestCard
                  key={record.id}
                  record={record}
                  isLibrary={isLibrary}
                  onOpen={setSelectedRecord}
                  onMarkDone={handleMarkDone}
                  onReopen={handleReopen}
                  onRequestDelete={handleRequestDelete}
                  isMarkingDone={markingDoneId === record.id}
                  isReopening={reopeningId === record.id}
                  isDeleting={deletingId === record.id}
                />
              ))}
            </div>
          )}

          {pagination.total > 0 && (
            <div className="fr-pagination">
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["5", "10", "20", "50"]}
                onChange={onPageChange}
                onShowSizeChange={onPageChange}
              />
            </div>
          )}
        </Col>
      </Row>

      <FeatureRequestDetailModal
        isOpen={!!selectedRecord}
        record={selectedRecord}
        isLibrary={isLibrary}
        onClose={() => setSelectedRecord(null)}
        onMarkDone={handleMarkDone}
        onReopen={handleReopen}
        onRequestDelete={handleRequestDelete}
        isMarkingDone={selectedRecord && markingDoneId === selectedRecord.id}
        isReopening={selectedRecord && reopeningId === selectedRecord.id}
        isDeleting={selectedRecord && deletingId === selectedRecord.id}
      />

      <FeatureRequestConfirmDeleteModal
        isOpen={!!deleteCandidate}
        record={deleteCandidate}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        isDeleting={!!deletingId}
      />
    </>
  );
};

const mapStateToProps = (state) => ({
  filter: state.filter,
});

export default connect(mapStateToProps, {
  getFeatureRequests,
  markFeatureRequestDone,
  reopenFeatureRequest,
  deleteFeatureRequest,
  toggleFilter,
})(FeatureRequestList);
